import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import PhoneInput, {
  splitE164ForDisplay,
  toInternationalPhoneDigits,
  isValidPhone,
} from '@/components/ui/phone-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { FieldError } from '@/components/ui/field-error';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type ClientDocType = 'ruc' | 'ci' | 'sin';

export interface ClientRow {
  id: string;
  name: string;
  docType: ClientDocType;
  ruc: string | null;
  razonSocial: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = crear; un cliente = editar. */
  initial?: ClientRow | null;
  /** Se llama con el cliente creado/editado (id + datos guardados). */
  onSaved?: (client: ClientRow) => void;
}

const empty = {
  name: '',
  docType: 'ruc' as ClientDocType,
  ruc: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
};

export function AppClientFormDialog({ open, onOpenChange, initial, onSaved }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [issues, setIssues] = useState<Record<string, string[]>>({});
  // Estado partido para el PhoneInput; `form.phone` queda como E.164 sin `+`.
  const [phoneCountry, setPhoneCountry] = useState('PY');
  const [phoneNational, setPhoneNational] = useState('');
  const phoneValid = phoneNational.trim() === '' || isValidPhone(phoneCountry, phoneNational); // true cuando vacío

  const handlePhone = (country: string, national: string) => {
    setPhoneCountry(country);
    setPhoneNational(national);
    setForm((f) => ({
      ...f,
      phone: national.trim() ? toInternationalPhoneDigits(country, national) : '',
    }));
  };

  // Lookup on-demand del RUC contra SET (botón "Buscar"). Si encuentra, autocompleta.
  const lookup = useMutation({
    mutationFn: () =>
      api<{ found: boolean; unverified: boolean; razonSocial: string | null }>(
        `/api/app/clients/lookup-ruc?ruc=${encodeURIComponent(form.ruc.trim())}`
      ),
    onSuccess: (r) => {
      if (r.found && r.razonSocial) {
        setForm((f) => ({ ...f, name: r.razonSocial! }));
        setIssues((i) => ({ ...i, ruc: [] }));
        toast.success(`Encontrado en SET: ${r.razonSocial}`);
      } else if (r.unverified) {
        toast.message('No se pudo consultar SET ahora. Cargá el nombre a mano o reintentá.');
      } else {
        toast.error('Ese RUC no aparece en el padrón de la SET.');
      }
    },
    onError: () => toast.error('No se pudo consultar el RUC'),
  });

  useEffect(() => {
    if (!open) return;
    setIssues({});
    const split = splitE164ForDisplay(initial?.phone);
    setPhoneCountry(split.countryCode);
    setPhoneNational(split.nationalNumber);
    setForm(
      initial
        ? {
            name: initial.name,
            docType: initial.docType,
            ruc: initial.ruc ?? '',
            email: initial.email ?? '',
            phone: initial.phone ?? '',
            address: initial.address ?? '',
            notes: initial.notes ?? '',
          }
        : empty
    );
  }, [open, initial]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim(),
        docType: form.docType,
        ruc: form.docType === 'sin' ? null : form.ruc.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      const r = initial
        ? await api<{ ok: true }>(`/api/app/clients/${initial.id}`, {
            method: 'PUT',
            body: JSON.stringify(body),
          }).then(() => ({ id: initial.id, razonSocial: initial.razonSocial }))
        : await api<{ id: string; razonSocial: string | null }>('/api/app/clients', {
            method: 'POST',
            body: JSON.stringify(body),
          });
      const saved: ClientRow = { id: r.id, razonSocial: r.razonSocial ?? null, ...body };
      return saved;
    },
    onSuccess: (saved) => {
      toast.success(initial ? 'Cliente actualizado' : 'Cliente creado');
      qc.invalidateQueries({ queryKey: ['app-clients'] });
      onSaved?.(saved);
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.status === 400) {
        const body = err.body as { error?: string; message?: string; issues?: Record<string, string[]> };
        if (body.error === 'ruc_not_found_in_set') {
          setIssues({ ruc: [body.message ?? 'El RUC no existe en SET.'] });
          return;
        }
        if (body.issues) {
          setIssues(body.issues);
          return;
        }
      }
      toast.error('No se pudo guardar el cliente');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    if (form.phone.trim() && !phoneValid) {
      setIssues({ phone: ['Número inválido para el país elegido.'] });
      return;
    }
    save.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{initial ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
            <DialogDescription>
              Si cargás un RUC, lo validamos contra el padrón de la SET.
            </DialogDescription>
          </DialogHeader>

          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="cl-name" required>
                Nombre / razón social
              </FieldLabel>
              <Input
                id="cl-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
              />
              <FieldError error={issues.name} />
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_1fr]">
              <Field>
                <FieldLabel htmlFor="cl-doctype">Documento</FieldLabel>
                <Select
                  value={form.docType}
                  onValueChange={(v) => setForm({ ...form, docType: v as ClientDocType })}
                >
                  <SelectTrigger id="cl-doctype">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ruc">RUC</SelectItem>
                    <SelectItem value="ci">CI</SelectItem>
                    <SelectItem value="sin">Sin documento</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.docType !== 'sin' && (
                <Field>
                  <FieldLabel htmlFor="cl-ruc">{form.docType === 'ruc' ? 'RUC' : 'CI'}</FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="cl-ruc"
                      value={form.ruc}
                      onChange={(e) => setForm({ ...form, ruc: e.target.value })}
                      placeholder={form.docType === 'ruc' ? '80012345-6' : '1234567'}
                    />
                    {form.docType === 'ruc' && (
                      <Button
                        type="button"
                        variant="outline"
                        className="shrink-0"
                        disabled={!form.ruc.trim() || lookup.isPending}
                        onClick={() => lookup.mutate()}
                        title="Buscar en SET y autocompletar la razón social"
                      >
                        <Search />
                        {lookup.isPending ? 'Buscando…' : 'Buscar'}
                      </Button>
                    )}
                  </div>
                  <FieldError error={issues.ruc} />
                </Field>
              )}
            </div>

            <Field>
              <FieldLabel htmlFor="cl-email">Email</FieldLabel>
              <Input
                id="cl-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="opcional"
              />
              <FieldError error={issues.email} />
            </Field>

            <Field>
              <FieldLabel htmlFor="cl-phone">Teléfono</FieldLabel>
              <PhoneInput
                id="cl-phone"
                showLabel={false}
                countryCode={phoneCountry}
                phoneNumber={phoneNational}
                onCountryCodeChange={(c) => handlePhone(c, phoneNational)}
                onPhoneNumberChange={(n) => handlePhone(phoneCountry, n)}
                placeholder="opcional"
              />
              <FieldError error={issues.phone} />
            </Field>

            <Field>
              <FieldLabel htmlFor="cl-address">Dirección</FieldLabel>
              <Input
                id="cl-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="opcional"
              />
            </Field>
          </FieldGroup>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={save.isPending || !form.name.trim()}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
